import type { FunctionComponent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import _ from 'underscore';

import { intl } from '@trello/i18n';
import { isSubmitEvent } from '@trello/keybindings';
import { Button } from '@trello/nachos/button';
import type { InvitationLinkPageIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { EmailRestrictionsList } from './EmailRestrictionsList';
import { isValidEmail, simpleDomainRestriction } from './HelperFunctions';
import { SwitchAccountsLink } from './SwitchAccountsLink';

interface InvalidEmailTextProps {
  returnUrl: string;
  requiredDomain: string | null;
  emailRestrictions: string[];
  setPendingEmail: (email: string) => void;
}

export const InvalidEmailText: FunctionComponent<InvalidEmailTextProps> = ({
  returnUrl,
  requiredDomain,
  emailRestrictions,
  setPendingEmail,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [formDisabled, setFormDisabled] = useState(false);

  const getEmail = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (text: any) => {
      if (text.length) {
        if (!requiredDomain) {
          return text;
        }

        return `${text}@${requiredDomain}`;
      }

      return '';
    },
    [requiredDomain],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onInputChange = useCallback((e: any) => {
    setInputValue(e.target.value);
  }, []);

  const handleSubmitEmail = useCallback(() => {
    setFormDisabled(true);

    const email = getEmail(inputValue);

    if (email) {
      // TODO this used to run POST /member/logins, but that
      // was removed ~2 years ago. Therefore, this feature
      // hasn't been working since then. There are plans to
      // update this feature but it's out of scope for now
      setPendingEmail(email);
    }
  }, [getEmail, inputValue, setPendingEmail]);

  const submitButtonDisabled =
    formDisabled || !isValidEmail(emailRestrictions, getEmail(inputValue));

  const onKeyDown = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      if (isSubmitEvent(e)) {
        if (!submitButtonDisabled) {
          handleSubmitEmail();
        }
      }
    },
    [handleSubmitEmail, submitButtonDisabled],
  );

  const firstDomain = useMemo(
    () =>
      _.chain(emailRestrictions)
        .map(simpleDomainRestriction)
        .compact()
        .first()
        .value(),
    [emailRestrictions],
  );

  const validatedEmailInput = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (placeholder: any, className: any) => {
      return (
        <input
          type="text"
          value={inputValue}
          placeholder={placeholder}
          onChange={onInputChange}
          className={className}
          disabled={formDisabled}
          onKeyDown={onKeyDown}
          data-testid={getTestId<InvitationLinkPageIds>('email-input')}
        />
      );
    },
    [formDisabled, inputValue, onInputChange, onKeyDown],
  );

  return (
    <>
      {requiredDomain ? (
        <>
          <p className="invitation-link-explanation">
            <FormattedMessage
              id="templates.invitation_link.to-join-this-team-you-need-to-confirm-an-email-from-domain-safe"
              defaultMessage="To join this Workspace, you'll need to add an email address from {domain} to your account."
              description="Add email address from domain to account"
              values={{
                domain: <strong key="required-domain">{requiredDomain}</strong>,
              }}
            />
          </p>
          <h2 className="add-email-to-your-account-heading">
            <FormattedMessage
              id="templates.invitation_link.add-email-to-your-account"
              defaultMessage="Add an email to your account"
              description="Add email to account"
            />
          </h2>
          <div className="invitation-link-add-email">
            {validatedEmailInput(
              intl.formatMessage({
                id: 'templates.invitation_link.yourname',
                defaultMessage: 'yourname',
                description: 'Your name',
              }),
              'invitation-link-add-email-prefix',
            )}
            <span className="invitation-link-add-email-domain">
              @{requiredDomain}
            </span>
          </div>
        </>
      ) : (
        <>
          <p className="invitation-link-explanation">
            <FormattedMessage
              id="templates.invitation_link.to-join-this-team-you-need-to-confirm-an-email-matching-one-of-the-following"
              defaultMessage="To join this Workspace, you'll need to confirm an email address that matches one of the following to your account:"
              description="Confirm email address matching the listed accounts"
            />
          </p>
          <EmailRestrictionsList emailRestrictions={emailRestrictions} />
          <h2 className="add-email-to-your-account-heading">
            <FormattedMessage
              id="templates.invitation_link.add-email-to-your-account"
              defaultMessage="Add an email to your account"
              description="Add email to account"
            />
          </h2>
          <div className="invitation-link-add-email">
            {validatedEmailInput(
              firstDomain
                ? intl.formatMessage(
                    {
                      id: 'templates.invitation_link.yourname-at-domain',
                      defaultMessage: 'yourname@{domain}',
                      description: 'Your name at domain',
                    },
                    {
                      domain: firstDomain,
                    },
                  )
                : undefined,
              'invitation-link-add-email',
            )}
          </div>
        </>
      )}
      <div className="invitation-link-options">
        <div>
          <Button
            className="nch-button nch-button--primary"
            onClick={handleSubmitEmail}
            isDisabled={submitButtonDisabled}
            testId={getTestId<InvitationLinkPageIds>('submit-email-input')}
          >
            <FormattedMessage
              id="templates.invitation_link.add-email"
              defaultMessage="Add email…"
              description="Add email"
            />
          </Button>
        </div>
        <div className="little-message">
          <SwitchAccountsLink returnUrl={returnUrl} />
        </div>
      </div>
    </>
  );
};
